import { Link } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button, ProgressBar } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import styles from './MesPouvoirs.module.css';
import { getActiveProfile } from '../lib/profiles';

/**
 * MesPouvoirs — écran 11/11 (D-NX-074, livré 2026-09-12).
 *
 * Vue « Mes pouvoirs » : profil magique révélé par le Diagnostic +
 * progression sur les chapitres. Minimaliste en Vague 2 — pas de loja, pas
 * de comparaison, pas d'inventaire (réserves pour vagues suivantes).
 *
 * Route canon : `/enfant/mes-pouvoirs/` (cf. `mte-architecture/04-routing.md` §6.2).
 *
 * Source spec Boussole :
 *  - `mte-product/03-screen-specs.md` (Écran 16 — `/enfant/mes-pouvoirs/`)
 *  - `mte-product/05-dashboard-map.md` (§7 — PowerButton, mes-pouvoirs)
 *  - `mte-product/06-empty-states-and-errors.md` (A4 — Sem progresso)
 *  - `mte-product/11-diagnostic-phrase-variants.md` (12 variantes canon)
 *
 * Marqueurs canon respectés :
 *  - 6 héros fixes (D-NX-038) via HERO_CELLS
 *  - Sábio mentor UNIVERSEL, NON sélectionnable (D-NX-042) — présent ici
 *    uniquement comme référence narrative (jamais sélectionnable)
 *  - Mundo único partilhé (D-NX-036 / M6)
 *  - Pas de comparaison entre enfants (espace strict de l'enfant courant)
 *  - Pas de loja, pas de troca de récompenses (D-IA-06 — réservé)
 *  - Pas de % ni note à l'enfant (D-OB-03) — uniquement libellés narratifs
 *  - AppBar Sortie parent TOUJOURS visible (D-UF-03)
 *  - Tap targets ≥ --touch-xl (96×96) sur cartes chapitres (D-PAL-CL-03)
 *  - Empty state : si Diagnostic pas fait, CTA « Découvrir mes pouvoirs »
 *    (jamais forcer l'enfant, D-NX-010)
 *
 * Mock Vague 2 (D-NX-066) :
 *  - Léo (CE1, ninja), Diagnostic FAIT (mock) → profils Curieux/Savant/Novice
 *  - Mission Rentrée étape 3/15 in_progress
 *  - P1-P5 locked (car MR non terminée)
 *  - Pas de persistance (PreviewAuthAdapter Vague 3)
 *
 * Vague staging M8 (Track A overnight 2026-09-13) — intégration spec Boussole §11.5 :
 *  - CE1 : 3 cartes Pattern A « Les sorts du X » (Maths / Français / QLM)
 *  - CM1 : 5 cartes Pattern A (Maths / Français / Sciences / HG / Exploration placeholder)
 *  - Labels = PROPOSITION-APPLIQUÉE (placeholder, Ana sign-off final pendente)
 */
type DiagnosticStatus = 'not_done' | 'done';
type Subject = 'maths' | 'francais' | 'sciences';
type NarrativeProfile = 'savant' | 'curieux' | 'futur' | 'novice';

interface SubjectProfile {
  subject: Subject;
  label: string;
  /** Profil révélé par le Diagnostic (placeholder Vague 2 si fait, "—" sinon). */
  profile: NarrativeProfile | null;
}

/* ===== Pattern A — Les sorts du X (Boussole spec §11.5) =====
 * Placeholder labels appliqués per D-PH2-01 (PROPOSITION-APPLIQUÉE).
 * CE1 = 3 matières ; CM1 = 5 matières + 1 case placeholder.
 * Noms pouvoir finaux = escalable Ana, NON canon até sign-off.
 */
type SortId =
  | 'sorts-calcul'
  | 'sorts-mots'
  | 'sorts-monde'
  | 'sorts-vivant'
  | 'sorts-terre-temps'
  | 'sorts-exploration';

interface PowerChip {
  id: SortId;
  label: string;
  matiere: string;
  /** Matière canon sous-jacente (référence interne / tooltip). */
  matiereId: 'maths' | 'francais' | 'qlm' | 'sciences' | 'hg' | 'exploration';
  /** placeholder EMC/Arts/EPS (à confirmer Lumen). */
  placeholder: boolean;
}

const POWERS_CE1: ReadonlyArray<PowerChip> = [
  { id: 'sorts-calcul',  label: 'Les sorts du calcul',       matiere: 'Mathématiques',        matiereId: 'maths',    placeholder: false },
  { id: 'sorts-mots',    label: 'Les sorts des mots',        matiere: 'Français',             matiereId: 'francais', placeholder: false },
  { id: 'sorts-monde',   label: 'Les sorts du monde',        matiere: 'Questionner le monde', matiereId: 'qlm',      placeholder: false },
];

const POWERS_CM1: ReadonlyArray<PowerChip> = [
  { id: 'sorts-calcul',      label: 'Les sorts du calcul',               matiere: 'Mathématiques',                       matiereId: 'maths',        placeholder: false },
  { id: 'sorts-mots',        label: 'Les sorts des mots',                matiere: 'Français',                            matiereId: 'francais',     placeholder: false },
  { id: 'sorts-vivant',      label: 'Les sorts du vivant',               matiere: 'Sciences (SCE-CM1)',                  matiereId: 'sciences',     placeholder: false },
  { id: 'sorts-terre-temps', label: 'Les sorts de la Terre et du temps', matiere: 'Histoire + Géographie (fusionnés HG)', matiereId: 'hg',           placeholder: false },
  { id: 'sorts-exploration', label: "Les sorts de l'exploration",        matiere: 'EMC · Arts · EPS (à confirmer Lumen)', matiereId: 'exploration', placeholder: true  },
];



const MOCK_DIAGNOSTIC_STATUS: DiagnosticStatus = 'done';

const MOCK_SUBJECTS: ReadonlyArray<SubjectProfile> = [
  { subject: 'maths', label: 'Maths', profile: 'curieux' },
  { subject: 'francais', label: 'Français', profile: 'savant' },
  { subject: 'sciences', label: 'Sciences', profile: 'novice' },
];

const TOTAL_MISSION_STEPS = 15;
const MOCK_MISSION_STEP = 3;

/* ===== PowerIcon — 6 SVG icons inline (Vague 3, ANA-DIRECT 13/09 ~22:15) =====
 * Ana autorizou criacao in-code sem pedir novos assets. Cada icone e alusivo a
 * materia do PowerChip correspondente, 24x24 viewBox renderizado a 20x20 via
 * className, fill currentColor para herdar a cor accent-${matiereId} aplicada
 * via CSS module (.powerIcon[data-matiere=...]). Aria-hidden pois o label
 * textual da materia ja esta presente na card. */
function PowerIcon({ id }: { id: SortId }): JSX.Element {
  if (id === 'sorts-calcul') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="power-icon-calcul">
        <rect x="4" y="3" width="16" height="18" rx="2" fill="currentColor" fillOpacity="0.12" />
        <rect x="7" y="6" width="10" height="3" rx="0.5" fill="currentColor" fillOpacity="0.6" />
        <circle cx="9" cy="13" r="1" fill="currentColor" />
        <circle cx="15" cy="13" r="1" fill="currentColor" />
        <circle cx="9" cy="17" r="1" fill="currentColor" />
        <circle cx="15" cy="17" r="1" fill="currentColor" />
        <circle cx="12" cy="13" r="1" fill="currentColor" />
        <circle cx="12" cy="17" r="1" fill="currentColor" />
      </svg>
    );
  }
  if (id === 'sorts-mots') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="power-icon-mots">
        <path d="M5 18 C 8 14, 11 22, 14 18 S 20 14, 21 12" strokeLinecap="round" />
        <circle cx="5" cy="6" r="1.5" fill="currentColor" />
        <path d="M5 6 L 9 6" />
        <path d="M19 18 L 19 14" />
      </svg>
    );
  }
  if (id === 'sorts-monde') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" data-testid="power-icon-monde">
        <circle cx="12" cy="12" r="8" fill="currentColor" fillOpacity="0.10" />
        <ellipse cx="12" cy="12" rx="8" ry="3" />
        <ellipse cx="12" cy="12" rx="3" ry="8" />
        <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      </svg>
    );
  }
  if (id === 'sorts-vivant') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="power-icon-vivant">
        <path d="M12 21 C 12 14, 4 11, 4 6 C 4 3, 8 3, 12 6 C 16 3, 20 3, 20 6 C 20 11, 12 14, 12 21 Z" fill="currentColor" fillOpacity="0.18" />
        <path d="M12 21 L 12 6" />
      </svg>
    );
  }
  if (id === 'sorts-terre-temps') {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="power-icon-terre-temps">
        <path d="M5 5 H 19 L 12 13 Z" fill="currentColor" fillOpacity="0.15" />
        <path d="M5 19 H 19 L 12 11 Z" />
        <path d="M5 5 H 19" />
        <path d="M5 19 H 19" />
      </svg>
    );
  }
  // exploration (placeholder EMC/Arts/EPS) — bússola estilizada
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-testid="power-icon-exploration">
      <circle cx="12" cy="12" r="8" fill="currentColor" fillOpacity="0.10" />
      <path d="M12 4 L 14 12 L 12 20 L 10 12 Z" fill="currentColor" />
      <path d="M4 12 L 12 10 L 20 12 L 12 14 Z" fill="currentColor" fillOpacity="0.6" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </svg>
  );
}
const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

function getHero(id: HeroId | null): HeroCell {
  if (!id) {
    return HERO_CELLS[0] as HeroCell;
  }
  const cell = HERO_BY_ID.get(id);
  if (!cell) {
    return HERO_CELLS[0] as HeroCell;
  }
  return cell;
}

const PROFILE_COPY: Record<NarrativeProfile, { title: string; phrase: string }> = {
  savant: {
    title: 'Savant',
    phrase: 'Tu reconnais vite les schémas et tu aimes les expliquer.',
  },
  curieux: {
    title: 'Curieux',
    phrase: 'Tu explores, tu compares, tu veux comprendre le pourquoi.',
  },
  futur: {
    title: 'Futur·e',
    phrase: 'Tu imagines la suite et tu relies les idées entre elles.',
  },
  novice: {
    title: 'Novice',
    phrase: 'Tu débutes — et c’est exactement pour ça que l’aventure commence.',
  },
};

interface ChapterProgress {
  id: string;
  title: string;
  status: 'done' | 'in_progress' | 'locked';
  /** 0..1 (used to feed ProgressBar in child view). */
  pct: number;
  href: string;
}

const CHAPTERS: ReadonlyArray<ChapterProgress> = [
  {
    id: 'mr',
    title: 'Mission Rentrée',
    status: 'in_progress',
    pct: MOCK_MISSION_STEP / TOTAL_MISSION_STEPS,
    href: '/enfant/mission/rentree/',
  },
  {
    id: 'diagnostic',
    title: 'Diagnostic',
    status: MOCK_DIAGNOSTIC_STATUS === 'done' ? 'done' : 'locked',
    pct: MOCK_DIAGNOSTIC_STATUS === 'done' ? 1 : 0,
    href: '/enfant/diagnostic/',
  },
  {
    id: 'p1',
    title: 'P1 — Les nombres',
    status: 'locked',
    pct: 0,
    href: '/enfant/aventure/',
  },
  {
    id: 'p2',
    title: 'P2 — Les mots',
    status: 'locked',
    pct: 0,
    href: '/enfant/aventure/',
  },
  {
    id: 'p3',
    title: 'P3 — Découvertes',
    status: 'locked',
    pct: 0,
    href: '/enfant/aventure/',
  },
  {
    id: 'p4',
    title: 'P4 — Grandir',
    status: 'locked',
    pct: 0,
    href: '/enfant/aventure/',
  },
  {
    id: 'p5',
    title: 'P5 — L’aventure continue',
    status: 'locked',
    pct: 0,
    href: '/enfant/aventure/',
  },
];

export default function MesPouvoirs(): JSX.Element {
  const profile = getActiveProfile();
  const hero = getHero(profile?.heroId ?? null);

  const appBar = (
    <Header
      context="enfant"
      user={
        <div className={styles.appBar} data-testid="app-bar">
          <div className={styles.appBarIdentity}>
            <HeroIllustration cell={hero} pose="standing" />
            <p className={styles.appBarHello}>
              <span className={styles.appBarHelloAccent}>{profile?.firstName ?? "Aventurier"}</span>
              {' · '}
              {hero.label}
            </p>
          </div>
          <Link
            to="/enfant/aventure/"
            className={styles.sortieLink}
            data-action="sortie-parent"
            aria-label="Sortie parent"
          >
            <Button tone="ghost" size="md" context="enfant">
              Sortie parent
            </Button>
          </Link>
        </div>
      }
    />
  );

  return (
    <Layout context="enfant" header={appBar} width="child">
      <div className={styles.eyebrowRow}>
        <p className={styles.eyebrow}>Mes pouvoirs</p>
        <p className={styles.screenLabel} aria-label="Écran 11 sur 11">11 / 11</p>
      </div>

      <section className={styles.hero} aria-labelledby="mes-pouvoirs-title">
        <div className={styles.heroIllustration} aria-hidden="true">
          <HeroIllustration cell={hero} pose="celebrate" />
        </div>
        <div className={styles.heroCopy}>
          <h1 id="mes-pouvoirs-title" className={styles.title}>
            Mes pouvoirs
          </h1>
          <p className={styles.subtitle}>
            Les sorts que tu apprends à maîtriser.
          </p>
        </div>
      </section>

      {MOCK_DIAGNOSTIC_STATUS === 'done' ? (
        <ProfileSection subjects={MOCK_SUBJECTS} />
      ) : (
        <EmptyDiagnostic />
      )}

      <PatternAPowers
        schoolLevel={profile?.schoolLevel ?? "CE1"}
        currentStep={MOCK_MISSION_STEP}
        totalSteps={TOTAL_MISSION_STEPS}
      />

      <Card>
        <div className={styles.chapters}>
          <p className={styles.sectionEyebrow}>Mes chapitres</p>
          <ul className={styles.chapterList} data-testid="chapter-list">
            {CHAPTERS.map((c) => (
              <li
                key={c.id}
                className={styles.chapter}
                data-status={c.status}
                data-chapter-id={c.id}
              >
                <div className={styles.chapterHeader}>
                  <span className={styles.chapterTitle}>{c.title}</span>
                  <span
                    className={styles.chapterStatus}
                    data-status={c.status}
                    aria-label={
                      c.status === 'done'
                        ? 'Terminé'
                        : c.status === 'in_progress'
                          ? 'En cours'
                          : 'À venir'
                    }
                  >
                    {c.status === 'done'
                      ? '✓ Terminé'
                      : c.status === 'in_progress'
                        ? '★ En cours'
                        : 'À venir'}
                  </span>
                </div>
                <ProgressBar
                  current={Math.round(c.pct * 100)}
                  total={100}
                  milestones={c.id === 'mr' ? [33, 67, 100] : undefined}
                  label={`Progression : ${c.title}`}
                />
                {c.status === 'in_progress' || c.status === 'done' ? (
                  <Link to={c.href} className={styles.chapterLink}>
                    <Button tone="ghost" size="sm" context="enfant">
                      {c.status === 'done' ? 'Revoir' : 'Continuer'}
                    </Button>
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <div className={styles.actions}>
        <Link to="/enfant/aventure/" data-action="retour-carte-monde">
          <Button tone="primary" size="lg" context="enfant" block>
            Retour à la carte du monde
          </Button>
        </Link>
        <Link to="/enfant/mission/rentree/" data-action="continuer-mr">
          <Button tone="ghost" size="md" context="enfant" block>
            Continuer la Mission Rentrée
          </Button>
        </Link>
      </div>
    </Layout>
  );
}

function PatternAPowers({
  schoolLevel,
  currentStep,
  totalSteps,
}: {
  schoolLevel: 'CP' | 'CE1' | 'CE2' | 'CM1' | 'CM2';
  currentStep: number;
  totalSteps: number;
}): JSX.Element {
  const powers = schoolLevel === 'CM1' ? POWERS_CM1 : POWERS_CE1;
  const gridClass =
    schoolLevel === 'CM1' ? styles.powersGridCm1 : styles.powersGridCe1;
  const mastery = Math.min(1, currentStep / totalSteps);
  return (
    <Card>
      <div className={styles.powers} data-testid="pattern-a-powers" data-school-level={schoolLevel}>
        <p className={styles.sectionEyebrow}>Mes sorts (Pattern A — placeholder)</p>
        <p className={styles.powersIntro}>
          Chaque matière réveille un sort. Tu progresses à ton rythme.
        </p>
        <ul className={gridClass} aria-label={`Sorts Pattern A pour ${schoolLevel}`}>
          {powers.map((p) => (
            <li
              key={p.id}
              className={styles.powerCard}
              data-sort-id={p.id}
              data-placeholder={p.placeholder ? 'true' : 'false'}
            >
              <div className={styles.powerHeader}>
                <span
                  className={styles.powerIcon}
                  data-matiere={p.matiereId}
                  aria-hidden="true"
                >
                  <PowerIcon id={p.id} />
                </span>
                <p className={styles.powerLabel}>{p.label}</p>
              </div>
              <p className={styles.powerMatiere}>{p.matiere}</p>
              <ProgressBar
                current={Math.round(mastery * 100)}
                total={100}
                label={`Maîtrise : ${p.label}`}
                milestones={[33, 67, 100]}
              />
              {p.placeholder ? (
                <p className={styles.powerPlaceholderNote}>
                  placeholder — à confirmer Lumen (EMC/Arts/EPS)
                </p>
              ) : null}
            </li>
          ))}
        </ul>
        <p className={styles.powersFooterNote}>
          Pattern A « Les sorts du X » · placeholder D-PH2-01 · sign-off Ana pendente
        </p>
      </div>
    </Card>
  );
}

function ProfileSection({
  subjects,
}: {
  subjects: ReadonlyArray<SubjectProfile>;
}): JSX.Element {
  return (
    <Card>
      <div className={styles.profiles} data-testid="profiles-section">
        <p className={styles.sectionEyebrow}>Mon profil magique</p>
        <p className={styles.profilesIntro}>
          Chaque matière réveille un pouvoir. Tu progresses à ton rythme.
        </p>
        <ul className={styles.profileList}>
          {subjects.map((s) => {
            const copy = s.profile ? PROFILE_COPY[s.profile] : null;
            return (
              <li
                key={s.subject}
                className={styles.profile}
                data-subject={s.subject}
                data-profile={s.profile ?? 'none'}
              >
                <p className={styles.profileSubject}>{s.label}</p>
                {copy ? (
                  <>
                    <p className={styles.profileTitle}>{copy.title}</p>
                    <p className={styles.profilePhrase}>{copy.phrase}</p>
                  </>
                ) : (
                  <p className={styles.profilePhrase}>
                    Le Sábio n’a pas encore vu cette matière en toi.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </Card>
  );
}

function EmptyDiagnostic(): JSX.Element {
  return (
    <Card>
      <div className={styles.empty} data-testid="empty-diagnostic">
        <p className={styles.sectionEyebrow}>Mon profil magique</p>
        <p className={styles.emptyTitle}>
          Tes pouvoirs sont prêts. Découvre-les dans le Diagnostic.
        </p>
        <p className={styles.emptyPhrase}>
          Le Sábio te montre ce que tu sais déjà, sans note ni jugement.
        </p>
        <Link to="/enfant/diagnostic/" data-action="decouvrir-pouvoirs">
          <Button tone="primary" size="lg" context="enfant">
            Découvrir mes pouvoirs
          </Button>
        </Link>
      </div>
    </Card>
  );
}
